/**
 * PDF Generation Service for SLAM Sports AI Analyzer
 * Uses jsPDF for reliable browser-based PDF generation
 * Supports team-specific branding via TeamConfig
 *
 * @module pdfService
 */

import { jsPDF } from 'jspdf';
import { defaultConfig } from './teamConfig';

/**
 * @typedef {import('./teamConfig').TeamConfig} TeamConfig
 */

/**
 * @typedef {Object} PDFOptions
 * @property {string} [filename] - Output filename
 * @property {number} [margin] - Page margin
 * @property {string} [playerName] - Player name for header
 * @property {string} [team] - Team name
 * @property {string} [markdownContent] - Raw markdown content
 * @property {TeamConfig} [teamConfig] - Team configuration
 */

/**
 * @typedef {Object} ParsedLine
 * @property {'h1' | 'h2' | 'h3' | 'bullet' | 'numbered' | 'table-header' | 'table-row' | 'table-separator' | 'paragraph' | 'empty'} type
 * @property {string} content
 * @property {number} [level]
 * @property {string[]} [cells]
 */

// Default SLAM brand colors (can be overridden by team config)
// Note: PDF uses WHITE background, so body text needs to be DARK
const DEFAULT_COLORS = {
  primary: [255, 212, 0],      // Gold - accents/headers
  headerText: [255, 255, 255], // White text on colored header
  bodyText: [30, 30, 35],      // Dark text for readability on white
  lightGray: [100, 100, 105],  // Secondary text
  gray: [120, 120, 125],       // Muted text
  darkGray: [60, 60, 65],      // Strong text
  black: [10, 10, 12],         // Headers on gold
};

/**
 * Load image as base64 data URL for PDF embedding
 * @param {string} url - Image URL
 * @returns {Promise<string|null>} Base64 data URL or null
 */
async function loadImageAsBase64(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const blob = await response.blob();

    // For SVG, we need to convert to PNG via canvas
    if (blob.type === 'image/svg+xml') {
      return new Promise((resolve) => {
        const img = new Image();
        const svgUrl = URL.createObjectURL(blob);

        img.onload = () => {
          const canvas = document.createElement('canvas');
          // Higher resolution for crisp logo
          canvas.width = 200;
          canvas.height = 200;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, 200, 200);
            resolve(canvas.toDataURL('image/png'));
          } else {
            resolve(null);
          }
          URL.revokeObjectURL(svgUrl);
        };

        img.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          resolve(null);
        };

        img.src = svgUrl;
      });
    }

    // For other images, convert directly to base64
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(/** @type {string} */ (reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error('[pdfService] Failed to load logo:', e);
    return null;
  }
}

/**
 * Convert hex color to RGB tuple
 * @param {string} hex - Hex color string
 * @returns {[number, number, number]} RGB tuple
 */
function hexToRgbTuple(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [255, 255, 255];
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ];
}

/**
 * Get colors from team config
 * PDF uses WHITE background, so we need dark text colors regardless of team config
 * @param {TeamConfig} [teamConfig] - Team configuration
 * @returns {Object} Color configuration
 */
function getTeamColors(teamConfig) {
  if (!teamConfig) return DEFAULT_COLORS;

  return {
    primary: hexToRgbTuple(teamConfig.colors.primary),          // Team primary for accents
    headerText: hexToRgbTuple(teamConfig.colors.text.onPrimary), // Text on primary bg
    bodyText: [30, 30, 35],          // ALWAYS dark for PDF readability
    lightGray: [100, 100, 105],
    gray: [120, 120, 125],
    darkGray: [60, 60, 65],
    black: [10, 10, 12],
  };
}

/**
 * Parse markdown into structured lines
 * @param {string} markdown - Markdown content
 * @returns {ParsedLine[]} Array of parsed lines
 */
const parseMarkdown = (markdown) => {
  const lines = markdown.split('\n');
  /** @type {ParsedLine[]} */
  const parsed = [];

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      parsed.push({ type: 'empty', content: '' });
      continue;
    }

    // Headers
    if (trimmed.startsWith('### ')) {
      parsed.push({ type: 'h3', content: trimmed.slice(4) });
    } else if (trimmed.startsWith('## ')) {
      parsed.push({ type: 'h2', content: trimmed.slice(3) });
    } else if (trimmed.startsWith('# ')) {
      parsed.push({ type: 'h1', content: trimmed.slice(2) });
    }
    // Bullet points
    else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const indent = line.search(/\S/);
      parsed.push({ type: 'bullet', content: trimmed.slice(2), level: Math.floor(indent / 2) });
    }
    // Numbered lists
    else if (/^\d+\.\s/.test(trimmed)) {
      parsed.push({ type: 'numbered', content: trimmed.replace(/^\d+\.\s/, '') });
    }
    // Tables
    else if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.slice(1, -1).split('|').map(c => c.trim());
      if (cells.every(c => /^[-:]+$/.test(c))) {
        parsed.push({ type: 'table-separator', content: trimmed, cells });
      } else if (parsed.length === 0 || parsed[parsed.length - 1].type !== 'table-row') {
        // First row after non-table content is header
        const prevNonEmpty = [...parsed].reverse().find(p => p.type !== 'empty' && p.type !== 'table-separator');
        if (!prevNonEmpty || (prevNonEmpty.type !== 'table-row' && prevNonEmpty.type !== 'table-header')) {
          parsed.push({ type: 'table-header', content: trimmed, cells });
        } else {
          parsed.push({ type: 'table-row', content: trimmed, cells });
        }
      } else {
        parsed.push({ type: 'table-row', content: trimmed, cells });
      }
    }
    // Regular paragraph
    else {
      parsed.push({ type: 'paragraph', content: trimmed });
    }
  }

  return parsed;
};

/**
 * Strip markdown formatting from text
 * @param {string} text - Text with markdown
 * @returns {string} Plain text
 */
const stripMarkdown = (text) => {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')  // Bold
    .replace(/\*([^*]+)\*/g, '$1')       // Italic
    .replace(/`([^`]+)`/g, '$1')         // Code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Links
};

/**
 * Draw text with word wrapping, returns new Y position
 * @param {jsPDF} doc - PDF document
 * @param {string} text - Text to draw
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} maxWidth - Maximum width
 * @param {number} lineHeight - Line height
 * @returns {number} New Y position
 */
const drawWrappedText = (doc, text, x, y, maxWidth, lineHeight) => {
  const cleanText = stripMarkdown(text);
  const lines = doc.splitTextToSize(cleanText, maxWidth);

  for (const line of lines) {
    if (y > 270) { // Near bottom of page
      doc.addPage();
      y = 20;
    }
    doc.text(line, x, y);
    y += lineHeight;
  }

  return y;
};

/**
 * Export markdown report to PDF
 * @param {HTMLElement|string} elementOrMarkdown - Element or markdown content
 * @param {PDFOptions} [options] - PDF options
 * @returns {Promise<void>}
 */
export const exportToPDF = async (elementOrMarkdown, options = {}) => {
  const {
    filename = generatePDFFilename('slam-analysis'),
    playerName,
    teamConfig = defaultConfig,
  } = options;

  // Get team-specific colors and branding
  const COLORS = getTeamColors(teamConfig);
  const teamName = teamConfig.branding.name;
  const reportTitle = teamConfig.analysis.terminology?.report || 'Analysis Report';

  // Get markdown content
  let markdown;
  if (typeof elementOrMarkdown === 'string') {
    markdown = elementOrMarkdown;
  } else if (options.markdownContent) {
    markdown = options.markdownContent;
  } else {
    // Try to extract text from element
    markdown = elementOrMarkdown.innerText || '';
  }

  // Create PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  let y = 20;

  // Header with team branding
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 28, 'F');

  // Logo configuration
  const logoSize = 18;
  const logoX = margin;
  const logoY = 5;
  let textStartX = margin; // Default if no logo

  // Try to load and add logo
  const logoUrl = teamConfig.logos?.primary || '/SSAILogo.svg';
  try {
    const logoBase64 = await loadImageAsBase64(logoUrl);
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', logoX, logoY, logoSize, logoSize);
      textStartX = margin + logoSize + 4; // Shift text to right of logo
    }
  } catch (e) {
    console.warn('[pdfService] Could not load logo, continuing without it');
  }

  // Use header text color on primary background
  doc.setTextColor(...COLORS.headerText);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(teamName.toUpperCase(), textStartX, 13);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(reportTitle, textStartX, 20);

  // Metadata on right side
  doc.setTextColor(...COLORS.headerText);
  doc.setFontSize(8);
  const dateStr = new Date().toLocaleString();
  doc.text(dateStr, pageWidth - margin, 13, { align: 'right' });
  if (playerName) {
    doc.text(`Player: ${playerName}`, pageWidth - margin, 20, { align: 'right' });
  }

  y = 38;

  // Parse and render markdown
  const parsed = parseMarkdown(markdown);
  let listCounter = 0;

  for (let i = 0; i < parsed.length; i++) {
    const line = parsed[i];

    // Check for page break
    if (y > 260) {
      doc.addPage();
      y = 20;
    }

    switch (line.type) {
      case 'h1':
        y += 4;
        doc.setFillColor(...COLORS.primary);
        doc.rect(margin - 2, y - 5, contentWidth + 4, 10, 'F');
        doc.setTextColor(...COLORS.black);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(stripMarkdown(line.content), margin, y);
        y += 12;
        break;

      case 'h2':
        y += 3;
        doc.setTextColor(...COLORS.primary);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(stripMarkdown(line.content), margin, y);
        // Underline
        doc.setDrawColor(...COLORS.primary);
        doc.setLineWidth(0.5);
        doc.line(margin, y + 1, margin + doc.getTextWidth(stripMarkdown(line.content)), y + 1);
        y += 8;
        break;

      case 'h3':
        y += 2;
        doc.setTextColor(...COLORS.darkGray);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(stripMarkdown(line.content), margin, y);
        y += 6;
        break;

      case 'bullet':
        doc.setTextColor(...COLORS.bodyText);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const bulletIndent = margin + (line.level || 0) * 4;
        doc.setFillColor(...COLORS.primary);
        doc.circle(bulletIndent + 1, y - 1, 0.8, 'F');
        y = drawWrappedText(doc, line.content, bulletIndent + 4, y, contentWidth - bulletIndent + margin - 4, 4);
        y += 1;
        break;

      case 'numbered':
        listCounter++;
        doc.setTextColor(...COLORS.primary);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`${listCounter}.`, margin, y);
        doc.setTextColor(...COLORS.bodyText);
        doc.setFont('helvetica', 'normal');
        y = drawWrappedText(doc, line.content, margin + 6, y, contentWidth - 6, 4);
        y += 1;
        break;

      case 'table-header':
        if (line.cells) {
          y += 2;
          doc.setFillColor(240, 240, 245);
          doc.rect(margin, y - 4, contentWidth, 6, 'F');
          doc.setTextColor(...COLORS.black);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          const headerCellWidth = contentWidth / line.cells.length;
          line.cells.forEach((cell, idx) => {
            const cellX = margin + (idx * headerCellWidth) + 2;
            doc.text(stripMarkdown(cell).slice(0, 20), cellX, y);
          });
          y += 4;
        }
        break;

      case 'table-row':
        if (line.cells) {
          doc.setTextColor(...COLORS.bodyText);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          const rowCellWidth = contentWidth / line.cells.length;
          line.cells.forEach((cell, idx) => {
            const cellX = margin + (idx * rowCellWidth) + 2;
            doc.text(stripMarkdown(cell).slice(0, 20), cellX, y);
          });
          // Row separator
          doc.setDrawColor(200, 200, 205);
          doc.setLineWidth(0.1);
          doc.line(margin, y + 1, margin + contentWidth, y + 1);
          y += 4;
        }
        break;

      case 'table-separator':
        // Skip separator rows
        break;

      case 'paragraph':
        // Reset list counter for non-list items
        const prevLine = parsed[i - 1];
        if (prevLine?.type !== 'numbered') {
          listCounter = 0;
        }
        doc.setTextColor(...COLORS.bodyText);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        y = drawWrappedText(doc, line.content, margin, y, contentWidth, 4);
        y += 2;
        break;

      case 'empty':
        y += 2;
        break;
    }
  }

  // Footer on all pages with team branding
  const totalPages = doc.internal.pages.length - 1;
  const platformName = teamConfig.analysis.terminology?.platform || teamConfig.branding.name;
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    // Dark footer background
    doc.setFillColor(30, 30, 35);
    doc.rect(0, 267, pageWidth, 15, 'F');
    // Light text on dark footer
    doc.setTextColor(180, 180, 185);
    doc.setFontSize(7);
    doc.text(`Generated by ${platformName} - ${reportTitle}`, margin, 274);
    doc.text(`Page ${page} of ${totalPages}`, pageWidth - margin, 274, { align: 'right' });
  }

  // Save PDF
  doc.save(filename);
  console.log('[pdfService] PDF generated successfully');
};

/**
 * Generate a filename for the PDF based on current date/time
 * @param {string} [prefix] - Filename prefix
 * @returns {string} Generated filename
 */
export const generatePDFFilename = (prefix = 'slam-pitch-analysis') => {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
  return `${prefix}-${dateStr}-${timeStr}.pdf`;
};

/**
 * Export to PDF and return as Blob (for email attachment)
 * @param {HTMLElement|string} elementOrMarkdown - Element or markdown content
 * @param {PDFOptions} [options] - PDF options
 * @returns {Promise<Blob>} PDF blob
 */
export const exportToPDFBlob = async (elementOrMarkdown, options = {}) => {
  const { teamConfig = defaultConfig } = options;
  const COLORS = getTeamColors(teamConfig);
  const teamName = teamConfig.branding.name;

  const markdown = typeof elementOrMarkdown === 'string'
    ? elementOrMarkdown
    : options.markdownContent || elementOrMarkdown.innerText || '';

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
  });

  // Simplified version for blob - just render text
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);

  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 20, 'F');
  doc.setTextColor(...COLORS.headerText);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${teamName.toUpperCase()} - Analysis Report`, margin, 13);

  // Body text - dark on white background
  doc.setTextColor(...COLORS.bodyText);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const lines = doc.splitTextToSize(stripMarkdown(markdown), contentWidth);
  let y = 30;

  for (const line of lines) {
    if (y > 270) {
      doc.addPage();
      y = 20;
    }
    doc.text(line, margin, y);
    y += 4;
  }

  return doc.output('blob');
};
